import { useRef, useState, useCallback, useEffect, useLayoutEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ShelterBrochure } from "@/components/ShelterBrochure";
import { Download, Loader2, ExternalLink, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";

const ForShelters = () => {
  const brochureRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(0.5);
  const [brochureSize, setBrochureSize] = useState({ width: 800, height: 1100 });
  const isMobile = useIsMobile();
  
  // Pinch-to-zoom state
  const initialPinchDistance = useRef<number | null>(null);
  const initialZoom = useRef<number>(0.5);

  const handleDownload = async () => {
    if (!brochureRef.current) return;

    setIsGenerating(true);
    try {
      const dataUrl = await toPng(brochureRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: "#ffffff",
      });

      const link = document.createElement("a");
      link.download = "GdzieSchronisko-Broszura.png";
      link.href = dataUrl;
      link.click();

      toast.success("Broszura została pobrana!");
    } catch (error) {
      console.error("Error generating brochure:", error);
      toast.error("Błąd podczas generowania broszury");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.15, 1));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.15, 0.35));
  };

  const handleResetZoom = () => {
    setZoomLevel(isMobile ? 0.5 : 1);
  };

  // Calculate distance between two touch points
  const getDistance = useCallback((touches: TouchList): number => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }, []);

  // Touch event handlers for pinch-to-zoom
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      initialPinchDistance.current = getDistance(e.touches);
      initialZoom.current = zoomLevel;
    }
  }, [getDistance, zoomLevel]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches.length === 2 && initialPinchDistance.current !== null) {
      e.preventDefault();
      const currentDistance = getDistance(e.touches);
      const scale = currentDistance / initialPinchDistance.current;
      const newZoom = Math.min(Math.max(initialZoom.current * scale, 0.35), 1);
      setZoomLevel(newZoom);
    }
  }, [getDistance]);

  const handleTouchEnd = useCallback(() => {
    initialPinchDistance.current = null;
  }, []);

  // Attach touch event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isMobile) return;

    container.addEventListener("touchstart", handleTouchStart, { passive: false });
    container.addEventListener("touchmove", handleTouchMove, { passive: false });
    container.addEventListener("touchend", handleTouchEnd);
    container.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [isMobile, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Measure brochure size so scaled preview doesn't leave empty space in layout
  useLayoutEffect(() => {
    const el = brochureRef.current;
    if (!el) return;

    const update = () => {
      const width = el.offsetWidth || 800;
      const height = el.offsetHeight || 1100;
      setBrochureSize({ width, height });
    };

    update();

    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    window.addEventListener("resize", update);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>Dla Schronisk - GdzieSchronisko</title>
        <meta name="description" content="Materiały promocyjne dla schronisk. Pobierz broszurę informacyjną i dołącz do platformy GdzieSchronisko, aby dotrzeć do wolontariuszy i darczyńców." />
        <link rel="canonical" href="https://gdzieschronisko.pl/dla-schronisk" />
      </Helmet>
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <PageBreadcrumbs currentPage="Dla schronisk" />

        {/* Download button - hidden on iOS */}
        {!isMobile && (
          <div className="text-center mb-6">
            <Button
              onClick={handleDownload}
              disabled={isGenerating}
              size="lg"
              className="gap-2"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generowanie...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Pobierz broszurę (PNG)
                </>
              )}
            </Button>
          </div>
        )}

        {/* Zoom controls - visible on mobile */}
        {isMobile && (
          <div className="flex flex-col items-center gap-2 mb-4">
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                disabled={zoomLevel <= 0.35}
                className="gap-1"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetZoom}
                className="gap-1 min-w-[80px]"
              >
                <RotateCcw className="w-4 h-4" />
                {Math.round(zoomLevel * 100)}%
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                disabled={zoomLevel >= 1}
                className="gap-1"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Użyj gestów rozciągania, aby powiększyć
            </p>
          </div>
        )}

        {/* Brochure preview */}
        <div 
          ref={containerRef}
          className="overflow-x-auto custom-scrollbar -mx-4 px-4 flex justify-center"
          style={{
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div 
            className="shadow-2xl rounded-xl overflow-hidden border border-border transition-transform duration-100 flex-shrink-0"
            style={{
              width: isMobile ? `${brochureSize.width * zoomLevel}px` : "800px",
              minWidth: isMobile ? `${brochureSize.width * zoomLevel}px` : "800px",
              height: isMobile ? `${brochureSize.height * zoomLevel}px` : undefined,
            }}
          >
            <div
              style={{
                width: "800px",
                transform: isMobile ? `scale(${zoomLevel})` : undefined,
                transformOrigin: "top left",
              }}
            >
              <ShelterBrochure ref={brochureRef} />
            </div>
          </div>
        </div>

        {/* Contact & CTA */}
        <div className="mt-10 max-w-2xl mx-auto text-center">
          <p className="text-muted-foreground mb-5">
            Potrzebujesz pomocy z aktualizacją potrzeb, dodaniem schroniska do bazy lub masz inne pytania dotyczące platformy? Napisz do nas: <strong className="text-foreground">kontakt@gdzieschronisko.pl</strong>
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="secondary" asChild>
              <Link to="/">Wróć do mapy schronisk</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/zglos" className="gap-2">
                <ExternalLink className="w-5 h-5" />
                Dodaj/zaktualizuj schronisko
              </Link>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ForShelters;
